// Redux
import { Action, Result, AsyncResult, ApiAction, Dispatch } from '../index';
import { getFundData } from './funds/fund-store';
import api from '../redux-api';
import { RESET_GIF } from './common/actions';
import { getBMOPortfolioHoldingsData, setComparisonIsFocused } from './common/actionCreators';

// Actions
import { replaceGifItemInComparison, saveComparison, setIsDirty } from './comparison';

// Utils
import { deepClone } from '../../utils/objects';
import { init as initI18n, formatMessage } from '../../utils/i18n';
import { GIF_PORTFOLIO_TYPES, GIF_PLAN_OPTIONS, GIF_DEPOSIT_TYPES, GIF_SCHEME_TYPES, SALES_CHARGES, WITHDRAWAL_TYPES } from '../../utils/bmo-gif/constants';
import { createScenario, getBaseDob, portfolioToBmoGifItem } from '../../utils/bmo-gif/index';
import { calculateBMOGifPortfolio } from '../../utils/bmo-gif/calculate';
import { getAge } from '../../utils/time';
import { FREQUENCIES, PROVINCES } from '../../utils/constants';

// Types
import { BMOGIFState } from './types/bmo-gif';
import Dictionary from '@/types/dictionary';
import ComparisonContact from '@/types/comparison-contact';
import BMOGIFProjectionBasis from '@/types/bmo-gif/bmo-gif-projection-basis';
import BMOGIFPortfolioTemplate from '@/types/bmo-gif/bmo-gif-portfolio-template';
import BMOGIFItem from '@/types/bmo-gif/bmo-gif-item';
import BMOGIFPortfolio from '@/types/bmo-gif/bmo-gif-portfolio';
import BMOGIFPortfolioHolding from '@/types/bmo-gif/bmo-gif-portfolio-holding';
import BMOGIFScenario from '@/types/bmo-gif/bmo-gif-scenario';
import Withdrawal from '@/types/bmo-gif/withdrawal';

// Since i18n might not be initialized yet, fetch messages directly and get the translation for
// the default portfolio name.
const { messages } = initI18n() as Dictionary<any>;

// Initial State
const INITIAL_STATE_PORTFOLIO: BMOGIFPortfolio = {
  name: messages['Portfolios.DefaultName'],
  holdings: [],
  portfolioType: GIF_PORTFOLIO_TYPES.CUSTOM,
  deathGuaranteeReset: false,
  deathGuaranteeResetEnhanced: false,
  depositType: GIF_DEPOSIT_TYPES.LUMP_SUM,
  purchaseDate: new Date(),
  scheme: GIF_SCHEME_TYPES.AGE,
  frequency: FREQUENCIES.MONTHLY,
  mer: 0,
  equityPercent: 0,
  deathBenefitFee: 0,
  dob: new Date(),
  marketReturn: [],
  averageMarketReturn: 0,
  isDefaultState: true,
  province: PROVINCES.FEDERAL,
};

const INITIAL_STATE: BMOGIFState = {
  portfolio: INITIAL_STATE_PORTFOLIO,
  projectionBasisOptions: [],
  templates: [],
  loading: {
    templates: false,
    projectionBasisOptions: false,
  },
  isDirty: false,
  showGraph: false,
};

// Actions
const SET_GIF_PORTFOLIO_HOLDINGS: Action = {
  type: 'connect/bmo-gif/set-gif-portfolio-holdings',
};

const ADD_HOLDING: Action = {
  type: 'connect/bmo-gif/holdings/add',
};

const REMOVE_HOLDING: Action = {
  type: 'connect/bmo-gif/holdings/remove',
};

const SET_NAME: Action = {
  type: 'connect/bmo-gif/name',
};

const SET_GIF_ITEM_NAME: Action = {
  type: 'connect/bmo-gif/set-gif-item-name',
};

const SET_GIF_PORTFOLIO: Action = {
  type: 'connect/bmo-gif/set-gif-portfolio',
};

const SET_GIF_ITEM: Action = {
  type: 'connect/bmo-gif/set-gif-item',
};

const SET_PLAN_OPTION: Action = {
  type: 'connect/bmo-gif/set-plan-option',
};

const SET_CLASS_TYPE: Action = {
  type: 'connect/bmo-gif/set-class-type',
};

const SET_PORTFOLIO_TYPE: Action = {
  type: 'connect/bmo-gif/set-portfolio-type',
};

const SET_DEATH_GUARANTEE_RESET: Action = {
  type: 'connect/bmo-gif/set-death-guarantee-reset',
};

const SET_DEATH_GUARANTEE_RESET_ENHANCED: Action = {
  type: 'connect/bmo-gif/set-death-guarantee-reset-enhanced',
};

const GET_GIF_TEMPLATES: ApiAction = {
  type: 'connect/bmo-gif/get-gif-templates',
  endpoint: 'bmo-gif/templates',
};

const GET_PROJECTION_BASIS_OPTIONS: ApiAction = {
  type: 'connect/bmo-gif/get-projection-basis-options',
  endpoint: 'bmo-gif/projection-basis',
};

const CLEAR_GIF_PORTFOLIO_HOLDINGS: Action = {
  type: 'connect/bmo-gif/clear-gif-portfolio-holdings',
};

const SET_PLAN_TERM: Action = {
  type: 'connect/bmo-gif/set-plan-term',
};

const SET_DEPOSIT_TYPE: Action = {
  type: 'connect/bmo-gif/set-deposit-type',
};

const SET_DEPOSIT_AMOUNT: Action = {
  type: 'connect/bmo-gif/set-deposit-amount',
};

const SET_PURCHASE_DATE: Action = {
  type: 'connect/bmo-gif/set-purchase-date',
};

const SET_CONTRACT_MATURITY_DATE: Action = {
  type: 'connect/bmo-gif/set-contract-maturity-date',
};

const SET_SUBSEQUENT_DEPOSIT_AMOUNT: Action = {
  type: 'connect/bmo-gif/set-subsequent-deposit-amount',
};

const SET_SCHEME_TYPE: Action = {
  type: 'connect/bmo-gif/set-scheme-type',
};

const SET_DEPOSIT_START: Action = {
  type: 'connect/bmo-gif/set-deposit-start',
};

const SET_DEPOSIT_END: Action = {
  type: 'connect/bmo-gif/set-deposit-end',
};

const SET_NUM_YEARS: Action = {
  type: 'connect/bmo-gif/set-num-years',
};

const SET_FREQUENCY: Action = {
  type: 'connect/bmo-gif/set-frequency',
};

const SET_PROJECTION_BASIS: Action = {
  type: 'connect/bmo-gif/set-projection-basis',
};

const SET_SALES_CHARGE_TYPE: Action = {
  type: 'connect/bmo-gif/set-sales-charge-type',
};

const SET_SALES_CHARGE_AMOUNT: Action = {
  type: 'connect/bmo-gif/set-sales-charge-amount',
};

const SET_SCENARIOS: Action = {
  type: 'connect/bmo-gif/set-scenarios',
};

const LOAD_SCENARIO_FOR_EDIT: Action = {
  type: 'connect/bmo-gif/load-scenario-for-edit',
};

const CANCEL_SCENARIO_EDITING: Action = {
  type: 'connect/bmo-gif/cancel-scenario-editing',
};

const SET_LUMP_SUM_WITHDRAWAL: Action = {
  type: 'connect/bmo-gif/set-lump-sum-withdrawal',
};

const SET_SYSTEMATIC_WITHDRAWAL: Action = {
  type: 'connect/bmo-gif/set-systematic-withdrawal',
};

const SET_BASE_DOB: Action = {
  type: 'connect/bmo-gif/set-base-dob',
};

const SET_SELECTED_SCENARIO: Action = {
  type: 'connect/bmo-gif/set-selected-scenario',
};

const UPDATE_SHOW_GRAPH: Action = {
  type: 'connect/bmo-gif/update-show-graph',
};

const SET_CUSTOM_RATE_AMOUNT: Action = {
  type: 'connect/bmo-gif/set-custom-rate-amount',
};

// Reducer
export default function reducer(state: BMOGIFState = INITIAL_STATE, action: Action): BMOGIFState {
  switch (action.type) {
    case SET_GIF_PORTFOLIO_HOLDINGS.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          holdings: action.holdings,
        },
        isDirty: true,
      };
    }
    case SET_NAME.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          name: action.name,
        },
        isDirty: true,
      };
    }
    case SET_GIF_ITEM_NAME.type: {
      if (!state.gifItem) {
        return state;
      }
      return {
        ...state,
        gifItem: {
          ...state.gifItem,
          name: action.name || formatMessage('Portfolios.Edit.NewPortfolioName'),
        },
      };
    }
    case SET_GIF_PORTFOLIO.type: {
      return {
        ...state,
        portfolio: {
          ...action.gifItem.portfolio,
          isDefaultState: false,
        },
        gifItem: action.gifItem,
      };
    }
    case SET_GIF_ITEM.type: {
      return {
        ...state,
        gifItem: action.item,
      };
    }
    case ADD_HOLDING.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          holdings: action.holdings,
        },
        isDirty: true,
      };
    }
    case REMOVE_HOLDING.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          holdings: action.holdings,
        },
        isDirty: true,
      };
    }
    case SET_PLAN_OPTION.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          planOption: action.value,
        },
      };
    }
    case SET_CLASS_TYPE.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          classType: action.value,
        },
      };
    }
    case SET_PORTFOLIO_TYPE.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          portfolioType: action.value,
        },
      };
    }
    case SET_DEATH_GUARANTEE_RESET.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          deathGuaranteeReset: action.value,
        },
      };
    }
    case SET_DEATH_GUARANTEE_RESET_ENHANCED.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          deathGuaranteeResetEnhanced: action.value,
        },
      };
    }
    case GET_GIF_TEMPLATES.type: {
      return {
        ...state,
        loading: {
          ...state.loading,
          templates: true,
        },
      };
    }
    case `${GET_GIF_TEMPLATES.type}_SUCCESS`: {
      return {
        ...state,
        templates: action.data.data,
        loading: {
          ...state.loading,
          templates: false,
        },
      };
    }
    case GET_PROJECTION_BASIS_OPTIONS.type: {
      return {
        ...state,
        loading: {
          ...state.loading,
          projectionBasisOptions: true,
        },
      };
    }
    case `${GET_PROJECTION_BASIS_OPTIONS.type}_SUCCESS`: {
      return {
        ...state,
        projectionBasisOptions: action.data.data,
        loading: {
          ...state.loading,
          projectionBasisOptions: false,
        },
      };
    }
    case CLEAR_GIF_PORTFOLIO_HOLDINGS.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          holdings: [],
        },
      };
    }
    case SET_PLAN_TERM.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          term: action.value,
        },
      };
    }
    case SET_DEPOSIT_TYPE.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          depositType: action.value,
        },
      };
    }
    case SET_DEPOSIT_AMOUNT.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          depositAmount: action.value,
        },
      };
    }
    case SET_PURCHASE_DATE.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          purchaseDate: action.value,
        },
      };
    }
    case SET_CONTRACT_MATURITY_DATE.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          contractMaturityDate: action.value,
        },
      };
    }
    case SET_SUBSEQUENT_DEPOSIT_AMOUNT.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          subsequentDepositAmount: action.value,
        },
      };
    }
    case SET_SCHEME_TYPE.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          scheme: action.value,
        },
      };
    }
    case SET_DEPOSIT_START.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          start: action.value,
        },
      };
    }
    case SET_DEPOSIT_END.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          end: action.value,
        },
      };
    }
    case SET_NUM_YEARS.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          numYears: action.value,
          end: action.end,
        },
      };
    }
    case SET_FREQUENCY.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          frequency: action.value,
        },
      };
    }
    case SET_PROJECTION_BASIS.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          projectionBasis: action.value,
        },
      };
    }
    case SET_SALES_CHARGE_TYPE.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          salesChargeType: action.value,
        },
      };
    }
    case SET_SALES_CHARGE_AMOUNT.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          salesChargeAmount: action.value,
        },
      };
    }
    case SET_SCENARIOS.type: {
      if (!state.gifItem) {
        return state;
      }
      return {
        ...state,
        gifItem: {
          ...state.gifItem,
          scenarios: action.scenarios,
        },
      };
    }
    case LOAD_SCENARIO_FOR_EDIT.type: {
      return {
        ...state,
        editedScenario: action.scenario,
      };
    }
    case CANCEL_SCENARIO_EDITING.type: {
      return {
        ...state,
        editedScenario: undefined,
      };
    }
    case SET_LUMP_SUM_WITHDRAWAL.type: {
      if (!state.editedScenario || !state.editedScenario.withdrawal) {
        return state;
      }

      return {
        ...state,
        editedScenario: {
          ...state.editedScenario,
          withdrawal: {
            ...state.editedScenario.withdrawal,
            lumpSum: action.lumpSum,
          },
        },
      };
    }
    case SET_SYSTEMATIC_WITHDRAWAL.type: {
      if (!state.editedScenario || !state.editedScenario.withdrawal) {
        return state;
      }

      return {
        ...state,
        editedScenario: {
          ...state.editedScenario,
          withdrawal: {
            ...state.editedScenario.withdrawal,
            systematic: action.systematic,
          },
        },
      };
    }
    case SET_BASE_DOB.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          dob: action.value,
        },
      };
    }
    case RESET_GIF.type: {
      return {
        ...state,
        portfolio: INITIAL_STATE_PORTFOLIO,
        gifItem: undefined,
        selectedScenario: undefined,
        showGraph: false,
      };
    }
    case SET_SELECTED_SCENARIO.type: {
      return {
        ...state,
        selectedScenario: action.scenario,
      };
    }
    case UPDATE_SHOW_GRAPH.type: {
      return {
        ...state,
        showGraph: action.value,
      };
    }
    case SET_CUSTOM_RATE_AMOUNT.type: {
      return {
        ...state,
        portfolio: {
          ...state.portfolio,
          customRate: action.value,
        },
      };
    }
    default:
      return state;
  }
}

export const clearGifPortfolioHoldings = (): Result<void> => dispatch => {
  dispatch(CLEAR_GIF_PORTFOLIO_HOLDINGS);
};

const updateGifTemplate = (dispatch: Dispatch, planOption?: string, classType?: string, portfolioType?: string, templates?: BMOGIFPortfolioTemplate[]) => {
  if (portfolioType && portfolioType !== GIF_PORTFOLIO_TYPES.CUSTOM) {
    if (planOption && classType && templates && templates.length) {
      const data = templates.find(
        template => template.classType === classType && template.planOption === planOption && template.portfolioType === portfolioType,
      );

      if (data) {
        dispatch({ type: SET_GIF_PORTFOLIO_HOLDINGS.type, holdings: data.holdings });
      }
    }
  }
};

// Action Creators
export const addFundToGifPortfolioById =
  (id: string): AsyncResult<void> =>
  async (dispatch, getState) => {
    const { portfolio, gifItem } = getState().bmoGif;

    const holdings = portfolio.holdings.slice();

    if (holdings.some(holding => holding.id === id)) {
      return Promise.reject({ errorKey: 'FUND_ALREADY_ADDED' });
    }

    const fund = await dispatch(getFundData(id));
    const holding = {
      symbol: fund.symbols && fund.symbols[0],
      id: fund._id,
      name: fund.names.full,
      weight: 0,
    } as BMOGIFPortfolioHolding;

    const newHoldings = holdings.concat([holding]);

    if (gifItem) {
      const item = {
        ...gifItem,
        portfolio: {
          ...gifItem.portfolio,
          holdings: newHoldings,
        },
      };
      dispatch({ ...SET_GIF_ITEM, item });
    }

    await dispatch({ type: ADD_HOLDING.type, holdings: newHoldings });
  };

export const allocateRemaining = (): Result<void> => (dispatch, getState) => {
  const { portfolio } = getState().bmoGif;
  const holdings = deepClone(portfolio.holdings) as BMOGIFPortfolioHolding[];

  const emptyHoldings = holdings.filter(h => h.weight === 0);
  const unallocatedHoldings = emptyHoldings.length ? emptyHoldings : holdings;
  const unallocatedWeight = 100 - holdings.reduce((total, holding) => total + holding.weight, 0);
  const remainder = unallocatedWeight / unallocatedHoldings.length;

  for (const unallocated of unallocatedHoldings) {
    const holding = holdings.find(h => h.id === unallocated.id);

    if (holding) {
      holding.weight += remainder;
    }
  }

  dispatch({ type: SET_GIF_PORTFOLIO_HOLDINGS.type, holdings });
};

export const removeHoldingFromGifPortfolioById =
  (holding: BMOGIFPortfolioHolding): Result<void> =>
  (dispatch, getState) => {
    const { portfolio, gifItem } = getState().bmoGif;

    const holdings = portfolio.holdings.slice();

    const newHoldings = holdings.filter(i => i.id !== holding.id);

    if (gifItem) {
      const item = {
        ...gifItem,
        portfolio: {
          ...gifItem.portfolio,
          holdings: newHoldings,
        },
      };
      dispatch({ ...SET_GIF_ITEM, item });
    }

    if (!newHoldings.length) {
      dispatch({ ...SET_GIF_ITEM, item: undefined });
    }

    dispatch({ type: REMOVE_HOLDING.type, holdings: newHoldings });
  };

export const changeHoldingWeight =
  (id: string, weight: number): Result<void> =>
  (dispatch, getState) => {
    const { portfolio } = getState().bmoGif;
    const holdings = deepClone(portfolio.holdings) as BMOGIFPortfolioHolding[];
    const holding = holdings.find(h => h.id === id);

    if (holding) {
      holding.weight = weight;
    }

    dispatch({ type: SET_GIF_PORTFOLIO_HOLDINGS.type, holdings });
  };

export const setPortfolioName =
  (name: string): Result<void> =>
  dispatch => {
    dispatch({ type: SET_NAME.type, name });
    dispatch(setIsDirty(true));
  };

export const setGifItemName =
  (name: string): Result<void> =>
  (dispatch, getState) => {
    const { gifItem } = getState().bmoGif;
    if (gifItem) {
      dispatch({ ...SET_GIF_ITEM_NAME, name });
    }
  };

export const setPlanTerm =
  (value: number): Result<void> =>
  dispatch => {
    dispatch({ ...SET_PLAN_TERM, value });
  };

export const setSalesChargeAmount =
  (value?: number): Result<void> =>
  dispatch => {
    dispatch({ ...SET_SALES_CHARGE_AMOUNT, value });
  };

export const setCustomRateAmount =
  (value?: number): Result<void> =>
  dispatch => {
    dispatch({ ...SET_CUSTOM_RATE_AMOUNT, value });
  };

export const setSalesChargeType =
  (value: string): Result<void> =>
  (dispatch, getState) => {
    const { salesChargeAmount } = getState().bmoGif.portfolio;

    if (value === SALES_CHARGES.FEL && !salesChargeAmount) {
      dispatch(setSalesChargeAmount(0));
    }

    dispatch({ ...SET_SALES_CHARGE_TYPE, value });
  };

export const setDeathGuaranteeReset =
  (value: boolean): Result<void> =>
  dispatch =>
    dispatch({ ...SET_DEATH_GUARANTEE_RESET, value });

export const setDeathGuaranteeResetEnhanced =
  (value: boolean): Result<void> =>
  dispatch =>
    dispatch({ ...SET_DEATH_GUARANTEE_RESET_ENHANCED, value });

export const setPlanOption =
  (value: string, dob: Date): Result<void> =>
  (dispatch, getState) => {
    const {
      portfolio: { portfolioType, classType, purchaseDate, planOption },
      templates,
    } = getState().bmoGif;

    updateGifTemplate(dispatch, value, classType, portfolioType, templates);

    if (planOption !== value && value === GIF_PLAN_OPTIONS.GIF100100) {
      dispatch(setPlanTerm(25));
      dispatch(setDeathGuaranteeReset(false));
    }

    if (value !== GIF_PLAN_OPTIONS.GIF100100) {
      const purchaseAge = getAge(dob, undefined, purchaseDate) || 0;
      dispatch(setPlanTerm(100 - purchaseAge));
    }

    if (value === GIF_PLAN_OPTIONS.GIF75100PLUS) {
      dispatch(setSalesChargeType(SALES_CHARGES.FEL));
      dispatch(setSalesChargeAmount(0));
    }
    if (value === GIF_PLAN_OPTIONS.GIF7575) {
      dispatch(setDeathGuaranteeReset(false));
    } else if (value === GIF_PLAN_OPTIONS.GIF75100 || value === GIF_PLAN_OPTIONS.GIF75100PLUS) {
      dispatch(setDeathGuaranteeReset(true));
    }

    return dispatch({ ...SET_PLAN_OPTION, value });
  };

export const setClassType =
  (value?: string): Result<void> =>
  (dispatch, getState) => {
    const {
      portfolio: { planOption, portfolioType },
      templates,
    } = getState().bmoGif;

    updateGifTemplate(dispatch, planOption, value, portfolioType, templates);

    return dispatch({ ...SET_CLASS_TYPE, value });
  };

export const setPortfolioType =
  (value?: string): Result<void> =>
  (dispatch, getState) => {
    const {
      portfolio: { planOption, classType },
      templates,
    } = getState().bmoGif;

    updateGifTemplate(dispatch, planOption, classType, value, templates);

    return dispatch({ ...SET_PORTFOLIO_TYPE, value });
  };

export const getGifTemplates = (): AsyncResult<BMOGIFPortfolioTemplate[]> => () => api.get(GET_GIF_TEMPLATES);

export const getProjectionBasisOptions = (): AsyncResult<BMOGIFProjectionBasis[]> => () => api.get(GET_PROJECTION_BASIS_OPTIONS);

export const loadGifPortfolioForEdit =
  (gifItem: BMOGIFItem): Result<void> =>
  dispatch => {
    dispatch({ ...SET_GIF_PORTFOLIO, gifItem });
  };

export const setDepositType =
  (value: string): Result<void> =>
  dispatch => {
    dispatch({ ...SET_DEPOSIT_TYPE, value });
  };

export const setDepositAmount =
  (value?: number): Result<void> =>
  dispatch => {
    dispatch({ ...SET_DEPOSIT_AMOUNT, value });
  };

export const setContractMaturityDate =
  (dob: Date): Result<void> =>
  (dispatch, getState) => {
    const {
      portfolio: { purchaseDate },
    } = getState().bmoGif;

    const baseAge = getAge(dob, undefined, purchaseDate);

    if (baseAge) {
      const purchaseYear = new Date(purchaseDate).getFullYear();
      const contractMaturityDateYear = purchaseYear + (100 - baseAge);
      const contractMaturityDate = new Date(`12/31/${contractMaturityDateYear}`);

      dispatch({ ...SET_CONTRACT_MATURITY_DATE, value: contractMaturityDate });
    }
  };

export const setPurchaseDate =
  (value: Date): Result<void> =>
  dispatch => {
    dispatch({ ...SET_PURCHASE_DATE, value });
  };

export const setSubsequentDepositAmount =
  (value: number): Result<void> =>
  dispatch => {
    dispatch({ ...SET_SUBSEQUENT_DEPOSIT_AMOUNT, value });
  };

export const setDepositEnd =
  (value?: number): Result<void> =>
  dispatch => {
    dispatch({ ...SET_DEPOSIT_END, value });
  };

export const setDepositStart =
  (value?: number): Result<void> =>
  (dispatch, getState) => {
    const { numYears } = getState().bmoGif.portfolio;

    dispatch({ ...SET_DEPOSIT_START, value });

    if (value === undefined || !numYears) {
      dispatch(setDepositEnd(undefined));
    }
    if (value && numYears) {
      const end = value + numYears;
      dispatch(setDepositEnd(end));
    }
  };

export const setNumYears =
  (value?: number): Result<void> =>
  (dispatch, getState) => {
    const { start } = getState().bmoGif.portfolio;

    dispatch({ ...SET_NUM_YEARS, value });

    if (start === undefined || value === undefined) {
      return dispatch(setDepositEnd(undefined));
    }

    const end = start + value;
    dispatch(setDepositEnd(end));
  };

export const setSchemeType =
  (value: string): Result<void> =>
  dispatch => {
    if (value === GIF_SCHEME_TYPES.YEAR) {
      dispatch(setDepositStart(undefined));
      dispatch(setDepositEnd(undefined));
    }

    if (value === GIF_SCHEME_TYPES.AGE) {
      dispatch(setDepositStart(undefined));
      dispatch(setDepositEnd(undefined));
      dispatch(setNumYears(undefined));
    }

    dispatch({ ...SET_SCHEME_TYPE, value });
  };

export const setFrequency =
  (value: string): Result<void> =>
  dispatch => {
    dispatch({ ...SET_FREQUENCY, value });
  };

export const setProjectionBasis =
  (value: { name: string; id: string }): Result<void> =>
  dispatch => {
    dispatch({ ...SET_PROJECTION_BASIS, value });
  };

export const loadScenarioForEdit =
  (scenario: BMOGIFScenario): Result<void> =>
  dispatch => {
    dispatch(setComparisonIsFocused(true));
    dispatch({ ...LOAD_SCENARIO_FOR_EDIT, scenario });
  };

export const cancelScenarioEditing = (): Result<void> => (dispatch, getState) => {
  const { editedScenario, gifItem } = getState().bmoGif;

  if (gifItem && editedScenario && editedScenario._id.startsWith('new-')) {
    const scenarios = gifItem.scenarios.slice();
    const newScenarios = scenarios.filter(scenario => scenario._id !== editedScenario._id);

    dispatch({ ...SET_SCENARIOS, scenarios: newScenarios });
  }

  dispatch({ ...CANCEL_SCENARIO_EDITING });
  dispatch(setComparisonIsFocused(false));
};

export const setLumpSumWithdrawalScheme =
  (value: string): Result<void> =>
  (dispatch, getState) => {
    const { editedScenario } = getState().bmoGif;

    if (!editedScenario || !editedScenario.withdrawal) {
      return;
    }

    const data = { ...editedScenario.withdrawal.lumpSum, ...editedScenario.withdrawal.lumpSum, scheme: value, at: undefined, month: 12 };

    dispatch({ ...SET_LUMP_SUM_WITHDRAWAL, lumpSum: data });
  };

export const setLumpSumWithdrawalAmount =
  (value: number): Result<void> =>
  (dispatch, getState) => {
    const { editedScenario } = getState().bmoGif;

    if (!editedScenario || !editedScenario.withdrawal) {
      return;
    }

    const data = { ...editedScenario.withdrawal.lumpSum, ...editedScenario.withdrawal.lumpSum, amount: value };

    dispatch({ ...SET_LUMP_SUM_WITHDRAWAL, lumpSum: data });
  };

export const setLumpSumWithdrawalAt =
  (value: number): Result<void> =>
  (dispatch, getState) => {
    const { editedScenario } = getState().bmoGif;

    if (!editedScenario || !editedScenario.withdrawal) {
      return;
    }

    const data = { ...editedScenario.withdrawal.lumpSum, ...editedScenario.withdrawal.lumpSum, at: value };

    dispatch({ ...SET_LUMP_SUM_WITHDRAWAL, lumpSum: data });
  };

export const setLumpSumWithdrawalMonth =
  (value?: number): Result<void> =>
  (dispatch, getState) => {
    const { editedScenario } = getState().bmoGif;

    if (!editedScenario || !editedScenario.withdrawal) {
      return;
    }

    const data = { ...editedScenario.withdrawal.lumpSum, ...editedScenario.withdrawal.lumpSum, month: value };

    dispatch({ ...SET_LUMP_SUM_WITHDRAWAL, lumpSum: data });
  };

export const setSystematicWithdrawalMonthStart =
  (value?: number): Result<void> =>
  (dispatch, getState) => {
    const { editedScenario } = getState().bmoGif;

    if (!editedScenario || !editedScenario.withdrawal) {
      return;
    }

    const data = { ...editedScenario.withdrawal.systematic, ...editedScenario.withdrawal.systematic, startMonth: value };

    dispatch({ ...SET_SYSTEMATIC_WITHDRAWAL, systematic: data });
  };

export const setSystematicWithdrawalMonthEnd =
  (value?: number): Result<void> =>
  (dispatch, getState) => {
    const { editedScenario } = getState().bmoGif;

    if (!editedScenario || !editedScenario.withdrawal) {
      return;
    }

    const data = { ...editedScenario.withdrawal.systematic, ...editedScenario.withdrawal.systematic, endMonth: value };

    dispatch({ ...SET_SYSTEMATIC_WITHDRAWAL, systematic: data });
  };

export const setSystematicWithdrawalAmount =
  (value: number): Result<void> =>
  (dispatch, getState) => {
    const { editedScenario } = getState().bmoGif;

    if (!editedScenario || !editedScenario.withdrawal) {
      return;
    }

    const data = { ...editedScenario.withdrawal.systematic, ...editedScenario.withdrawal.systematic, amount: value };

    dispatch({ ...SET_SYSTEMATIC_WITHDRAWAL, systematic: data });
  };

export const setSystematicWithdrawalType =
  (value: string): Result<void> =>
  (dispatch, getState) => {
    const { editedScenario } = getState().bmoGif;

    if (!editedScenario || !editedScenario.withdrawal) {
      return;
    }

    const isAgeScheme = value === WITHDRAWAL_TYPES.RRIF || value === WITHDRAWAL_TYPES.LIF_MAX;

    const data = {
      ...editedScenario.withdrawal.systematic,
      ...editedScenario.withdrawal.systematic,
      type: value,
      scheme: isAgeScheme ? GIF_SCHEME_TYPES.AGE : editedScenario.withdrawal.systematic.scheme,
    };

    dispatch({ ...SET_SYSTEMATIC_WITHDRAWAL, systematic: data });
  };

export const setSystematicWithdrawalScheme =
  (value: string): Result<void> =>
  (dispatch, getState) => {
    const { editedScenario } = getState().bmoGif;

    if (!editedScenario || !editedScenario.withdrawal) {
      return;
    }

    const data = {
      ...editedScenario.withdrawal.systematic,
      ...editedScenario.withdrawal.systematic,
      scheme: value,
      start: undefined,
      end: undefined,
      numYears: undefined,
      startMonth: 1,
      endMonth: 12,
    };

    dispatch({ ...SET_SYSTEMATIC_WITHDRAWAL, systematic: data });
  };

export const setSystematicWithdrawalEnd =
  (value?: number): Result<void> =>
  (dispatch, getState) => {
    const { editedScenario } = getState().bmoGif;

    if (!editedScenario || !editedScenario.withdrawal) {
      return;
    }

    const data = { ...editedScenario.withdrawal.systematic, ...editedScenario.withdrawal.systematic, end: value };

    dispatch({ ...SET_SYSTEMATIC_WITHDRAWAL, systematic: data });
  };

export const setSystematicWithdrawalStart =
  (value?: number): Result<void> =>
  (dispatch, getState) => {
    const { editedScenario } = getState().bmoGif;

    if (!editedScenario || !editedScenario.withdrawal) {
      return;
    }

    const data = { ...editedScenario.withdrawal.systematic, ...editedScenario.withdrawal.systematic, start: value };

    dispatch({ ...SET_SYSTEMATIC_WITHDRAWAL, systematic: data });

    if (value === undefined || !editedScenario.withdrawal.systematic.numYears) {
      return;
    }

    const end = value + editedScenario.withdrawal.systematic.numYears;
    dispatch(setSystematicWithdrawalEnd(end));
  };

export const setSystematicWithdrawalYears =
  (value?: number): Result<void> =>
  (dispatch, getState) => {
    const { editedScenario } = getState().bmoGif;

    if (!editedScenario || !editedScenario.withdrawal) {
      return;
    }

    const data = { ...editedScenario.withdrawal.systematic, ...editedScenario.withdrawal.systematic, numYears: value };

    dispatch({ ...SET_SYSTEMATIC_WITHDRAWAL, systematic: data });

    if (!editedScenario.withdrawal.systematic.start || !value) {
      dispatch(setSystematicWithdrawalEnd(undefined));
      return;
    }

    const end = editedScenario.withdrawal.systematic.start + value;
    dispatch(setSystematicWithdrawalEnd(end));
  };

export const setSystematicWithdrawalFrequency =
  (value: string): Result<void> =>
  (dispatch, getState) => {
    const { editedScenario } = getState().bmoGif;

    if (!editedScenario || !editedScenario.withdrawal) {
      return;
    }

    const data = { ...editedScenario.withdrawal.systematic, ...editedScenario.withdrawal.systematic, frequency: value, startMonth: 1, endMonth: 12 };

    dispatch({ ...SET_SYSTEMATIC_WITHDRAWAL, systematic: data });
  };

export const setBaseDob =
  (contact: ComparisonContact): Result<void> =>
  (dispatch, getState) => {
    const {
      portfolio: { planOption },
    } = getState().bmoGif;
    const baseDob = getBaseDob(contact.dob || new Date());

    dispatch(setContractMaturityDate(baseDob));
    if (planOption) {
      dispatch(setPlanOption(planOption, baseDob));
    }
    dispatch({ ...SET_BASE_DOB, value: baseDob });
  };

export const selectScenario =
  (scenario: BMOGIFScenario): Result<void> =>
  (dispatch, getState) => {
    const { selectedScenario } = getState().bmoGif;

    if (selectedScenario && selectedScenario._id === scenario._id) {
      return dispatch({ ...SET_SELECTED_SCENARIO, scenario: undefined });
    }

    dispatch({ ...SET_SELECTED_SCENARIO, scenario });
  };

export const calculateBMOPortfolio =
  (autoSave?: boolean): AsyncResult<void> =>
  async (dispatch, getState) => {
    const { comparison, bmoGif } = getState();
    const { portfolio, gifItem, selectedScenario } = bmoGif;

    const data = await dispatch(getBMOPortfolioHoldingsData(portfolio));
    const newPortfolio = calculateBMOGifPortfolio(portfolio, data);
    newPortfolio.province = comparison.activeComparison?.contact?.province;
    const newItem = portfolioToBmoGifItem(newPortfolio);

    if (gifItem && gifItem.scenarios.length) {
      const scenarios: BMOGIFScenario[] = [];
      for (const scenario of gifItem.scenarios) {
        scenarios.push(createScenario(newPortfolio, scenario.withdrawal, false));
      }
      newItem.scenarios = scenarios;
    }

    // When recalculating a portfolio the new scenarios created won't match the scenario that might be selected
    if (selectedScenario && gifItem) {
      const baseScenarioSelected = gifItem.baseScenario._id === selectedScenario._id;

      if (baseScenarioSelected) {
        dispatch(selectScenario(newItem.baseScenario));
      } else {
        const oldIndex = gifItem.scenarios.findIndex(s => s._id === selectedScenario._id);

        dispatch(selectScenario(newItem.scenarios[oldIndex]));
      }
    }

    dispatch({ ...SET_GIF_PORTFOLIO, gifItem: newItem });
    dispatch(replaceGifItemInComparison(newItem));

    if (autoSave) {
      dispatch(saveComparison());
    }
  };

export const createNewScenario = (): AsyncResult<void> => async (dispatch, getState) => {
  const {
    bmoGif: { portfolio, gifItem },
  } = getState();

  if (!gifItem) {
    return;
  }

  const withdrawal: Withdrawal = {
    lumpSum: {
      scheme: GIF_SCHEME_TYPES.AGE,
      amount: 0,
      at: 0,
      month: 12,
    },
    systematic: {
      scheme: GIF_SCHEME_TYPES.AGE,
      amount: 0,
      numYears: 0,
      end: 0,
      frequency: FREQUENCIES.MONTHLY,
      type: WITHDRAWAL_TYPES.CUSTOM,
      startMonth: 1,
      endMonth: 12,
    },
  };

  const scenario = createScenario(portfolio, withdrawal, true);

  const scenarios = gifItem.scenarios.slice();
  const newScenarios = scenarios.concat([scenario]);

  dispatch(loadScenarioForEdit(scenario));
  dispatch({ ...SET_SCENARIOS, scenarios: newScenarios });
};

export const saveScenario = (): Result<void> => (dispatch, getState) => {
  const { editedScenario, gifItem } = getState().bmoGif;

  if (!editedScenario || !gifItem) {
    return;
  }

  const scenarios = gifItem.scenarios.slice();

  const indexToReplace = scenarios.findIndex(s => s._id === editedScenario._id);

  scenarios[indexToReplace] = editedScenario;
  dispatch({ ...SET_SCENARIOS, scenarios });
  dispatch(calculateBMOPortfolio());
  dispatch({ ...CANCEL_SCENARIO_EDITING });
  dispatch(setComparisonIsFocused(false));
};

export const deleteScenario =
  (scenario: BMOGIFScenario): Result<void> =>
  (dispatch, getState) => {
    const { gifItem, selectedScenario } = getState().bmoGif;
    const newItem = deepClone(gifItem);

    if (!gifItem || !newItem) {
      return;
    }

    if (selectedScenario && selectedScenario._id === scenario._id) {
      dispatch({ ...SET_SELECTED_SCENARIO, scenario: undefined });
    }

    const scenarios = gifItem.scenarios.slice();

    const newScenarios = scenarios.filter(s => s._id !== scenario._id);
    newItem.scenarios = newScenarios;

    if (!newScenarios.length) {
      dispatch({ ...SET_SELECTED_SCENARIO, scenario: gifItem.baseScenario });
    }

    dispatch({ ...SET_SCENARIOS, scenarios: newScenarios });
    dispatch(replaceGifItemInComparison(newItem));
  };

export const updateShowGraph =
  (value: boolean): Result<void> =>
  dispatch => {
    dispatch({ ...UPDATE_SHOW_GRAPH, value });

    if (value === false) {
      dispatch({ ...SET_SELECTED_SCENARIO, scenario: undefined });
    }
  };
